import * as Sentry from '@sentry/react';

/**
 * Frontend Sentry initialisation.
 *
 * Safe no-op when VITE_SENTRY_DSN is not set (e.g. local dev without a DSN).
 *
 * Replay configuration: we capture session replays ONLY when an error occurs
 * (not random sessions), and all text is masked and all media blocked by
 * default because Attendza renders HR-sensitive data (names, salaries,
 * documents, leave reasons). Developers investigating a crash see the
 * structure of the page, not its contents.
 */
export function initSentry(): void {
  const dsn = import.meta.env["VITE_SENTRY_DSN"] as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env["VITE_ENVIRONMENT"] as string | undefined) ?? 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy defaults — do NOT loosen without explicit review.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    // Never capture random-session replays. Only capture when an error fires,
    // so we stay well within the Sentry free-tier replay budget and avoid
    // recording HR workflows routinely.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

export { Sentry };
